import { useEffect, useRef } from 'react';
import styles from './HomePage.module.css';

/**
 * Canvas «rete animata» di sfondo dell'hero (`.ic-ds-net` del riferimento):
 * punti che derivano lentamente, collegati quando vicini.
 *
 * È DECORAZIONE PURA: `aria-hidden`, nessuna informazione. Il comando che la
 * governa («Disattiva animazione», WCAG 2.2.2) sta nell'hero, in HomePage:
 * qui arriva solo la prop `attiva`. Con `attiva=false` il canvas resta
 * fermo sull'ultimo fotogramma disegnato in statico.
 *
 * In jsdom `getContext` restituisce null: ogni accesso è guardato.
 */

interface Punto {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

const N_PUNTI = 48;
const SOGLIA_LINK = 130;

export interface HeroNetworkProps {
  readonly attiva: boolean;
}

export function HeroNetwork({ attiva }: HeroNetworkProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const puntiRef = useRef<Punto[] | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let larghezza = 0;
    let altezza = 0;
    let rafId = 0;

    const ridimensiona = () => {
      const rect = canvas.getBoundingClientRect();
      larghezza = Math.max(1, Math.round(rect.width));
      altezza = Math.max(1, Math.round(rect.height));
      canvas.width = larghezza;
      canvas.height = altezza;
    };
    ridimensiona();

    if (!puntiRef.current) {
      puntiRef.current = Array.from({ length: N_PUNTI }, () => ({
        x: Math.random() * larghezza,
        y: Math.random() * altezza,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
      }));
    }
    const punti = puntiRef.current;

    const disegna = () => {
      ctx.clearRect(0, 0, larghezza, altezza);
      for (let i = 0; i < punti.length; i += 1) {
        const a = punti[i]!;
        for (let j = i + 1; j < punti.length; j += 1) {
          const b = punti[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < SOGLIA_LINK * SOGLIA_LINK) {
            const alpha = 0.14 * (1 - Math.sqrt(d2) / SOGLIA_LINK);
            ctx.strokeStyle = `rgba(91, 196, 255, ${alpha.toFixed(3)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      ctx.fillStyle = 'rgba(91, 196, 255, 0.45)';
      for (const p of punti) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const passo = () => {
      for (const p of punti) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > larghezza) p.vx = -p.vx;
        if (p.y < 0 || p.y > altezza) p.vy = -p.vy;
      }
      disegna();
      rafId = window.requestAnimationFrame(passo);
    };

    // Un fotogramma statico c'è sempre; il loop solo se l'animazione è attiva.
    disegna();
    if (attiva) rafId = window.requestAnimationFrame(passo);

    const osservatore =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            ridimensiona();
            disegna();
          });
    osservatore?.observe(canvas);

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      osservatore?.disconnect();
    };
  }, [attiva]);

  return <canvas ref={canvasRef} className={styles.heroCanvas} aria-hidden="true" />;
}
